import { useMemo } from 'react'
import { AlertTriangle, CheckCircle2, Clock, TrendingDown } from 'lucide-react'
import { parseDate, diffDays } from './ganttUtils'
import type { CommessaStrategia } from './GanttCommesse'

interface CommessaSemaforo {
  commessa: CommessaStrategia
  stato_semaforo: 'verde' | 'giallo' | 'rosso'
  giorni_ritardo: number
  perc_completamento: number
  motivo: string
}

interface Props {
  commesse: CommessaStrategia[]
}

const STATI_LABEL: Record<string, string> = {
  in_esecuzione: 'In Esecuzione',
  pianificazione: 'Pianificazione',
  in_revisione: 'In Revisione',
  approvata: 'Approvata',
}

export default function SemaforoCommesse({ commesse }: Props) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const items = useMemo<CommessaSemaforo[]>(() => {
    return commesse.map(c => {
      const fasi_totali = c.fasi.length
      const fasi_ok = c.fasi.filter(f => f.completata).length
      const perc = fasi_totali > 0 ? Math.round(fasi_ok / fasi_totali * 100) : 0

      // Data fine Gantt = ultima data_fine_prevista tra le fasi
      const date_fine = c.fasi
        .filter(f => f.data_fine_prevista)
        .map(f => parseDate(f.data_fine_prevista!))
      const data_fine_gantt = date_fine.length > 0 ? new Date(Math.max(...date_fine.map(d => d.getTime()))) : null
      const data_consegna = c.data_prevista_consegna ? parseDate(c.data_prevista_consegna) : null

      let stato_semaforo: 'verde' | 'giallo' | 'rosso' = 'verde'
      let giorni_ritardo = 0
      let motivo = 'In linea con i piani'

      if (data_fine_gantt && data_consegna) {
        giorni_ritardo = diffDays(data_consegna, data_fine_gantt)
        if (giorni_ritardo > 14) {
          stato_semaforo = 'rosso'
          motivo = `Gantt sfora di ${giorni_ritardo}g`
        } else if (giorni_ritardo > 0) {
          stato_semaforo = 'giallo'
          motivo = `Margine ridotto: ${giorni_ritardo}g`
        }
      } else if (!data_consegna) {
        stato_semaforo = 'giallo'
        motivo = 'Data consegna non definita'
      }

      // Controlla fasi in ritardo
      const fasi_in_ritardo = c.fasi.filter(f => f.in_ritardo).length
      if (fasi_in_ritardo > 0 && stato_semaforo === 'verde') {
        stato_semaforo = 'giallo'
        motivo = `${fasi_in_ritardo} fase/i in ritardo`
      }

      return { commessa: c, stato_semaforo, giorni_ritardo, perc_completamento: perc, motivo }
    }).sort((a, b) => {
      const order = { rosso: 0, giallo: 1, verde: 2 }
      return order[a.stato_semaforo] - order[b.stato_semaforo]
    })
  }, [commesse])

  const counts = {
    rosso: items.filter(i => i.stato_semaforo === 'rosso').length,
    giallo: items.filter(i => i.stato_semaforo === 'giallo').length,
    verde: items.filter(i => i.stato_semaforo === 'verde').length,
  }

  if (items.length === 0) {
    return <div className="text-center text-gray-400 py-16">Nessuna commessa attiva</div>
  }

  return (
    <div className="space-y-6">
      {/* Summary bar */}
      <div className="flex gap-4">
        {[
          { key: 'rosso', label: 'Critiche', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
          { key: 'giallo', label: 'Attenzione', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-400' },
          { key: 'verde', label: 'In linea', color: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' },
        ].map(s => (
          <div key={s.key} className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${s.color}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
            <span className="text-2xl font-bold">{counts[s.key as keyof typeof counts]}</span>
            <span className="text-sm font-medium">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Grid cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map(({ commessa: c, stato_semaforo, perc_completamento, motivo }) => {
          const borderColor = stato_semaforo === 'rosso' ? 'border-red-400' : stato_semaforo === 'giallo' ? 'border-amber-400' : 'border-green-400'
          const bgHeader = stato_semaforo === 'rosso' ? 'bg-red-50' : stato_semaforo === 'giallo' ? 'bg-amber-50' : 'bg-green-50'
          const textColor = stato_semaforo === 'rosso' ? 'text-red-600' : stato_semaforo === 'giallo' ? 'text-amber-600' : 'text-green-600'
          const Icon = stato_semaforo === 'rosso' ? AlertTriangle : stato_semaforo === 'giallo' ? Clock : CheckCircle2

          return (
            <div key={c.id} className={`border-2 ${borderColor} rounded-xl overflow-hidden bg-white shadow-sm`}>
              {/* Header */}
              <div className={`${bgHeader} px-4 py-3 flex items-start justify-between gap-2`}>
                <div className="min-w-0">
                  <span className="font-mono text-xs font-bold text-gray-500">{c.numero_commessa}</span>
                  <p className="font-semibold text-gray-900 text-sm truncate mt-0.5">{c.titolo}</p>
                  <p className="text-xs text-gray-500 truncate">{c.cliente_nome}</p>
                </div>
                <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${textColor}`} />
              </div>

              {/* Body */}
              <div className="px-4 py-3 space-y-3">
                {/* Progress bar */}
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Avanzamento fasi</span>
                    <span className="font-medium">{perc_completamento}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${stato_semaforo === 'verde' ? 'bg-green-500' : stato_semaforo === 'giallo' ? 'bg-amber-400' : 'bg-red-400'}`}
                      style={{ width: `${perc_completamento}%` }}
                    />
                  </div>
                </div>

                {/* Info row */}
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium ${textColor}`}>{motivo}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${bgHeader} ${textColor}`}>
                    {STATI_LABEL[c.stato] || c.stato}
                  </span>
                </div>

                {/* Consegna */}
                {c.data_prevista_consegna && (
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <TrendingDown className="w-3 h-3" />
                    Consegna: {new Date(c.data_prevista_consegna + 'T00:00:00').toLocaleDateString('it-IT')}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
